import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Trash2, Pencil, Plus, Search, MapPin, Map as MapIcon, Grid, X, Check, ChevronDown, Layers, AlertCircle, AlertTriangle, ArrowUpDown } from 'lucide-react';
import { API_URL } from '../config';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import ParkingMap from './ParkingMap';

const CustomFilterSelect = ({ label, value, onChange, options, allLabel = "ทั้งหมด", icon: Icon }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedOption = options.find(opt => String(opt.value) === String(value));

    return (
        <div className="w-full md:w-auto min-w-[140px] relative" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`w-full bg-white border rounded-lg shadow-sm flex items-center px-2.5 py-1.5 gap-2 text-left ${isOpen ? '' : ''
                    }`}
            >
                {Icon && (
                    <div className={`p-1 rounded-lg flex-shrink-0 transition-colors ${isOpen ? 'bg-blue-100' : 'bg-blue-50'}`}>
                        <Icon className="w-3.5 h-3.5 text-blue-600" />
                    </div>
                )}
                <div className="flex flex-col w-full min-w-0">
                    <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wider leading-none mb-1">{label}</span>
                    <span className="text-xs font-bold text-slate-800">
                        {selectedOption?.label || allLabel}
                    </span>
                </div>
                <div className={`text-slate-300 transition-transform duration-200 ${isOpen ? 'rotate-180 text-blue-500' : ''}`}>
                    <ChevronDown className="h-3 w-3" />
                </div>
            </button>

            {isOpen && (
                <div className="absolute z-[1000] mt-1.5 min-w-full w-max max-w-[240px] left-0 bg-white border border-slate-100 rounded-[1.5rem] shadow-2xl py-1.5 overflow-hidden animate-in fade-in zoom-in-95 duration-200 origin-top">
                    {allLabel && (
                        <>
                            <button
                                onClick={() => { onChange(''); setIsOpen(false); }}
                                className={`w-full text-left px-4 py-2 text-[11px] font-bold flex items-center justify-between transition-colors ${(!value || value === 'All') ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                            >
                                <span>{allLabel}</span>
                                {(!value || value === 'All') && <Check className="w-3.5 h-3.5" />}
                            </button>
                            <div className="h-px bg-slate-100 my-1" />
                        </>
                    )}
                    <div className="max-h-[200px] overflow-y-auto scrollbar-hide">
                        {options.map((opt, idx) => (
                            <button
                                key={idx}
                                onClick={() => { onChange(opt.value); setIsOpen(false); }}
                                className={`w-full text-left px-4 py-2 text-[11px] font-bold flex items-center justify-between transition-colors ${String(value) === String(opt.value) ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                            >
                                <span className="truncate">{opt.label}</span>
                                {String(value) === String(opt.value) && <Check className="w-3.5 h-3.5" />}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const SlotManagement = ({ token, role, username, setToken, permissions = [] }) => {
    const [slots, setSlots] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [zones, setZones] = useState([]);
    const [filterZone, setFilterZone] = useState('');
    const [filterStatus] = useState('All');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [newSlot, setNewSlot] = useState({ slot_name: '', zone_id: '', vehicle_type: 'Car', remark: '', is_activate: true, is_double_parking: false, coordinate_x: null, coordinate_y: null });
    const [error, setError] = useState('');
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showErrorPopup, setShowErrorPopup] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState(null);
    const [viewMode, setViewMode] = useState('Grid'); // 'Grid' or 'Map'
    const [isRowsDropdownOpen, setIsRowsDropdownOpen] = useState(false);
    const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
    const [isZoneDropdownOpen, setIsZoneDropdownOpen] = useState(false);
    const [isVehicleTypeDropdownOpen, setIsVehicleTypeDropdownOpen] = useState(false);
    const [isDoubleParkingDropdownOpen, setIsDoubleParkingDropdownOpen] = useState(false);
    const navigate = useNavigate();

    const [editingSlot, setEditingSlot] = useState(null);

    // Filters State
    const [sortConfig, setSortConfig] = useState({ key: 'slot_name', direction: 'asc' });

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-20" />;
        return sortConfig.direction === 'asc' ?
            <ChevronDown className="w-3 h-3 ml-1 text-indigo-600 rotate-180" /> :
            <ChevronDown className="w-3 h-3 ml-1 text-indigo-600" />;
    };

    const fetchSlots = React.useCallback(async () => {
        try {
            const res = await axios.get(`${API_URL}/api/slots`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSlots(res.data);
        } catch (err) {
            console.error(err);
            if (err.response && err.response.status === 403) {
                const msg = err.response.data?.message || err.response.data?.error || 'Access Denied';
                alert(msg);
                navigate('/');
            }
        }
    }, [token, navigate]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchSlots();
            // Fetch Zones
            axios.get(`${API_URL}/api/zones`, {
                headers: { Authorization: `Bearer ${token}` }
            }).then(res => {
                setZones(res.data);
                if (res.data.length > 0 && !filterZone) {
                    const activeZones = res.data.filter(z => z.is_active);
                    if (activeZones.length > 0) {
                        setFilterZone(activeZones[0].id);
                    }
                }
            }).catch(err => console.error(err));
        }, 50);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchSlots, token]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterZone, filterStatus, rowsPerPage]);

    const handleSave = async (e) => {
        e.preventDefault();

        // Frontend Validation
        if (!newSlot.slot_name.trim()) {
            setError('กรุณาระบุชื่อช่องจอด');
            return;
        }
        if (!newSlot.zone_id) {
            setError('กรุณาเลือกโซน');
            return;
        }

        setError('');
        setSuccessMessage('');

        try {
            const payload = {
                slot_name: newSlot.slot_name.trim(),
                zone_id: newSlot.zone_id,
                vehicle_type: newSlot.vehicle_type,
                remark: newSlot.remark || '',
                is_activate: newSlot.is_activate,
                is_double_parking: newSlot.is_double_parking ? 1 : 0,
                coordinate_x: (newSlot.coordinate_x !== null && newSlot.coordinate_x !== undefined) ? parseFloat(newSlot.coordinate_x) : null,
                coordinate_y: (newSlot.coordinate_y !== null && newSlot.coordinate_y !== undefined) ? parseFloat(newSlot.coordinate_y) : null
            };

            const token = localStorage.getItem('token');
            if (editingSlot) {
                await axios.put(`${API_URL}/api/slots/${editingSlot.id}`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSuccessMessage(`อัปเดตข้อมูลช่องจอด ${payload.slot_name} เรียบร้อยแล้ว`);
                setShowSuccessPopup(true);
            } else {
                await axios.post(`${API_URL}/api/slots`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSuccessMessage(`เพิ่มข้อมูลช่องจอด ${payload.slot_name} เรียบร้อยแล้ว`);
                setShowSuccessPopup(true);
            }

            setNewSlot({ slot_name: '', zone_id: '', vehicle_type: 'Car', remark: '', is_activate: true, is_double_parking: false, coordinate_x: null, coordinate_y: null });
            setEditingSlot(null);
            fetchSlots();
            setIsModalOpen(false); // Close modal on success
        } catch (err) {
            if (err.response?.status === 401) return;
            console.error('Save Slot Error:', err);
            const backendMsg = err.response?.data?.message || (typeof err.response?.data === 'string' ? err.response.data : null);
            setError(backendMsg || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล');
        }
    };

    const handleDelete = (id) => {
        setDeleteTargetId(id);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`${API_URL}/api/slots/${deleteTargetId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSuccessMessage('ลบข้อมูลช่องจอดเรียบร้อยแล้ว');
            setShowSuccessPopup(true);
            setShowDeleteConfirm(false);
            fetchSlots();
        } catch (err) {
            console.error('Delete Error:', err.response?.data || err.message);
            if (err.response?.status === 401) return;
            const errorMsg = err.response?.data || 'เกิดข้อผิดพลาดในการลบข้อมูล';
            setErrorMessage(errorMsg);
            setShowErrorPopup(true);
            setShowDeleteConfirm(false);
        }
        setDeleteTargetId(null);
    };

    const openModal = (slot = null, initialCoords = null) => {
        setIsModalOpen(true);
        setError('');
        setSuccessMessage('');
        if (slot) {
            setEditingSlot(slot);
            setNewSlot({
                slot_name: slot.slot_name,
                zone_id: slot.zone_id,
                vehicle_type: slot.vehicle_type || 'Car',
                remark: slot.remark || '',
                is_activate: slot.is_activate === 1 || slot.is_activate === true,
                is_double_parking: slot.is_double_parking === 1 || slot.is_double_parking === true,
                coordinate_x: slot.coordinate_x,
                coordinate_y: slot.coordinate_y
            });
        } else {
            setEditingSlot(null);
            // If current filterZone is set, use it as default
            const defaultZone = filterZone || (activeZones.length > 0 ? activeZones[0].id : '');
            setNewSlot({
                slot_name: '',
                zone_id: defaultZone,
                vehicle_type: 'Car',
                remark: '',
                is_activate: true,
                is_double_parking: false,
                coordinate_x: initialCoords ? initialCoords.x : null,
                coordinate_y: initialCoords ? initialCoords.y : null
            });
        }
    };

    const activeZones = zones.filter(z => z.is_active).sort((a, b) => a.display_order - b.display_order);

    // Filter Slots
    const filteredSlots = slots.filter(s => {
        // 1. Search Name
        if (searchTerm && !s.slot_name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
        // 2. Filter Zone (use zone_id)
        if (filterZone && String(s.zone_id) !== String(filterZone)) return false;
        // 3. Filter Status
        if (filterStatus !== 'All') {
            const isActive = s.is_activate === 1 || s.is_activate === true;
            if (filterStatus === 'Active' && !isActive) return false;
            if (filterStatus === 'Inactive' && isActive) return false;
        }
        return true;
    });

    // Sort Logic
    const sortedSlots = [...filteredSlots].sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle nulls
        if (aValue === null || aValue === undefined) aValue = '';
        if (bValue === null || bValue === undefined) bValue = '';

        if (sortConfig.key === 'slot_name') {
            return sortConfig.direction === 'asc'
                ? aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: 'base' })
                : bValue.localeCompare(aValue, undefined, { numeric: true, sensitivity: 'base' });
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    const totalItems = sortedSlots.length;
    const totalPages = Math.ceil(totalItems / rowsPerPage);
    const currentSlots = sortedSlots.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    const menuPerm = permissions.find(p => p.role.toLowerCase() === role.toLowerCase() && p.menu_key === 'slots');
    const canEdit = menuPerm ? (menuPerm.can_edit === 1 || menuPerm.can_edit === true) : false;

    return (
        <div className="bg-slate-50 min-h-screen pb-10 text-slate-900">
            <Navbar role={role} username={username} onLogout={() => setToken(null)} permissions={permissions} />

            <div className="max-w-7xl mx-auto px-4 md:px-8 space-y-6">
                {/* View Toggle & Actions Bar */}
                <div className="relative md:sticky md:top-[64px] z-[100] bg-slate-50/80 md:backdrop-blur-sm pt-1.5 pb-2 mb-2 -mx-2 px-2 md:-mx-4 md:px-4 ">
                    <div className="bg-white p-3 sm:p-4 rounded-xl shadow-md border border-slate-200">
                        <div className="flex flex-wrap gap-2 sm:gap-4 items-center">
                            {/* View Switcher */}
                            <div className="flex bg-slate-100 p-1 rounded-lg">
                                <button
                                    onClick={() => setViewMode('Grid')}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-bold  ${viewMode === 'Grid' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    <Grid className="w-3.5 h-3.5" />
                                    <span>Grid</span>
                                </button>
                                <button
                                    onClick={() => setViewMode('Map')}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-bold  ${viewMode === 'Map' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    <MapIcon className="w-3.5 h-3.5" />
                                    <span>Map</span>
                                </button>
                            </div>

                            {/* Search */}
                            <div className="relative w-full md:w-64">
                                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                    <Search className="w-4 h-4 text-slate-400" />
                                </div>
                                <input
                                    type="text"
                                    placeholder="ค้นหาช่องจอด..."
                                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-200 outline-none text-sm"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            {/* Filter Zone */}
                            <div className="w-full md:w-auto">
                                <CustomFilterSelect
                                    label="โซน"
                                    icon={Layers}
                                    allLabel={null} // Zone usually needs one selected
                                    value={filterZone}
                                    onChange={setFilterZone}
                                    options={activeZones.map(z => ({ label: z.zone_name, value: z.id }))}
                                />
                            </div>

                            <div className="ml-auto">
                                {canEdit && (
                                    <button
                                        onClick={() => openModal(null)}
                                        className="w-full md:w-auto flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg font-bold text-[10px] shadow-md shadow-blue-500/10  active:scale-95 border border-blue-500/20"
                                    >
                                        <Plus className="w-4 h-4 shrink-0" />
                                        <span className="truncate">เพิ่มช่องจอด</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {viewMode === 'Map' ? (
                    <div className="w-full h-[500px] md:h-[600px] rounded-3xl overflow-hidden shadow-xl border border-slate-200">
                        <ParkingMap
                            slots={filteredSlots}
                            selectedZone={zones.find(z => String(z.id) === String(filterZone))?.zone_name || ''}
                            logs={{}}
                            onSlotClick={(id) => canEdit ? openModal(slots.find(s => s.id === id)) : null}
                            isAdmin={canEdit}
                            token={token}
                            onRefresh={fetchSlots}
                            zones={zones}
                            onZoneChange={(zoneName) => {
                                const zone = zones.find(z => z.zone_name === zoneName);
                                if (zone) setFilterZone(zone.id);
                            }}
                            onMapClickToAdd={(x, y) => openModal(null, { x, y })}
                        />
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                            <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                                Slot Management
                            </h2>
                            <span className="text-[9px] font-bold text-slate-500 bg-slate-200 px-2 py-1 rounded-full">{filteredSlots.length} ช่อง</span>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse table-fixed min-w-[1000px]">
                                <thead>
                                    <tr>
                                        <th onClick={() => requestSort('slot_name')} className="w-[80px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-center cursor-pointer hover:bg-slate-100 transition-colors">
                                            <div className="flex items-center justify-center gap-1">ช่องจอด {getSortIcon('slot_name')}</div>
                                        </th>
                                        <th onClick={() => requestSort('vehicle_type')} className="w-[70px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-center cursor-pointer hover:bg-slate-100 transition-colors">
                                            <div className="flex items-center justify-center gap-1">ประเภท {getSortIcon('vehicle_type')}</div>
                                        </th>
                                        <th onClick={() => requestSort('is_double_parking')} className="w-[90px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-center cursor-pointer hover:bg-slate-100 transition-colors">
                                            <div className="flex items-center justify-center gap-1">รูปแบบจอด {getSortIcon('is_double_parking')}</div>
                                        </th>
                                        <th onClick={() => requestSort('coordinate_x')} className="w-[60px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-center cursor-pointer hover:bg-slate-100 transition-colors">
                                            <div className="flex items-center justify-center gap-1">X (%) {getSortIcon('coordinate_x')}</div>
                                        </th>
                                        <th onClick={() => requestSort('coordinate_y')} className="w-[60px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-center cursor-pointer hover:bg-slate-100 transition-colors">
                                            <div className="flex items-center justify-center gap-1">Y (%) {getSortIcon('coordinate_y')}</div>
                                        </th>
                                        <th onClick={() => requestSort('rotation')} className="w-[80px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-center cursor-pointer hover:bg-slate-100 transition-colors">
                                            <div className="flex items-center justify-center gap-1">Rot (°) {getSortIcon('rotation')}</div>
                                        </th>
                                        <th onClick={() => requestSort('remark')} className="min-w-[150px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-left cursor-pointer hover:bg-slate-100 transition-colors">
                                            <div className="flex items-center gap-1">หมายเหตุ {getSortIcon('remark')}</div>
                                        </th>
                                        <th className="w-auto"></th>
                                        <th onClick={() => requestSort('created_date')} className="w-[120px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-left cursor-pointer hover:bg-slate-100 transition-colors">
                                            <div className="flex items-center gap-1">วันที่สร้าง {getSortIcon('created_date')}</div>
                                        </th>
                                        <th onClick={() => requestSort('updated_date')} className="w-[120px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-left cursor-pointer hover:bg-slate-100 transition-colors">
                                            <div className="flex items-center gap-1">วันที่อัปเดต {getSortIcon('updated_date')}</div>
                                        </th>
                                        <th onClick={() => requestSort('is_activate')} className="w-[90px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-center cursor-pointer hover:bg-slate-100 transition-colors">
                                            <div className="flex items-center justify-center gap-1">สถานะ {getSortIcon('is_activate')}</div>
                                        </th>
                                        <th className="w-[70px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-wider text-right pr-4"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {currentSlots.length === 0 ? (
                                        <tr>
                                            <td colSpan="12" className="p-8 text-center text-slate-500">
                                                <div className="flex flex-col items-center justify-center gap-2">
                                                    <Search className="w-10 h-10 text-slate-300" />
                                                    <p className="font-medium text-sm">ไม่พบช่องจอด</p>
                                                    <p className="text-xs text-slate-400">ลองเปลี่ยนเงื่อนไขการค้นหาใหม่</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        currentSlots.map(s => (
                                            <tr key={s.id} className="hover:bg-slate-50/50 transition-colors group">
                                                <td className="px-1 py-1 align-middle whitespace-nowrap text-center">
                                                    <span className="text-[11px] font-black text-slate-700 leading-none">{s.slot_name}</span>
                                                </td>
                                                <td className="px-1 py-1 align-middle whitespace-nowrap">
                                                    <div className="flex items-center justify-center text-center">
                                                        <span className="text-[11px] font-black text-slate-700 leading-none">
                                                            {s.vehicle_type === 'Motorcycle' ? 'จยย.' : 'รถยนต์'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-1 py-1 align-middle whitespace-nowrap">
                                                    <div className="flex items-center justify-center text-center">
                                                        <span className="text-[11px] font-black text-slate-700 leading-none">
                                                            {(s.is_double_parking === 1 || s.is_double_parking === true) ? 'ซ้อนคัน' : 'ปกติ'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-1 py-1 align-middle whitespace-nowrap">
                                                    <div className="flex items-center justify-center text-center font-bold">
                                                        <span className="text-[11px] font-black leading-none text-slate-700">
                                                            {s.coordinate_x !== null ? Number(s.coordinate_x).toFixed(1) : '-'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-1 py-1 align-middle whitespace-nowrap">
                                                    <div className="flex items-center justify-center text-center font-bold">
                                                        <span className="text-[11px] font-black leading-none text-slate-700">
                                                            {s.coordinate_y !== null ? Number(s.coordinate_y).toFixed(1) : '-'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-1 py-1 align-middle whitespace-nowrap">
                                                    <div className="flex items-center justify-center text-center font-bold">
                                                        <span className="text-[11px] font-black leading-none text-slate-700">
                                                            {s.rotation !== null ? `${Number(s.rotation)}°` : '0°'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-1 py-1 align-middle">
                                                    <div className="flex items-center max-w-[150px]">
                                                        <span className="text-[10px] font-bold text-slate-400 truncate leading-none" title={s.remark}>
                                                            {s.remark || <span className="text-slate-200 font-black italic select-none">-</span>}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="w-auto"></td>
                                                <td className="px-1 py-1 align-middle whitespace-nowrap">
                                                    <div className="flex flex-col justify-center leading-none items-start">
                                                        <span className="text-[10px] font-black text-slate-700 tracking-tight">
                                                            {s.created_date ? new Date(s.created_date).toLocaleString('th-TH', { day: 'numeric', month: 'numeric', year: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '') : '-'}
                                                        </span>
                                                        <span className="text-[8px] text-slate-400 mt-1 tracking-wider">by {s.created_by_name || '-'}</span>
                                                    </div>
                                                </td>
                                                <td className="px-1 py-1 align-middle whitespace-nowrap">
                                                    <div className="flex flex-col justify-center leading-none items-start">
                                                        <span className="text-[10px] font-black text-slate-700 tracking-tight">
                                                            {s.updated_date ? new Date(s.updated_date).toLocaleString('th-TH', { day: 'numeric', month: 'numeric', year: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '') : '-'}
                                                        </span>
                                                        <span className="text-[8px] text-slate-400 mt-1 tracking-wider">by {s.updated_by_name || '-'}</span>
                                                    </div>
                                                </td>
                                                <td className="px-1 py-1 align-middle whitespace-nowrap">
                                                    <div className="flex items-center justify-center text-center">
                                                        <span className={`px-1.5 py-0.5 rounded-full text-[8px] font-bold tracking-wide border ${(s.is_activate === 1 || s.is_activate === true)
                                                            ? 'bg-green-50 text-green-700 border-green-200'
                                                            : 'bg-rose-50 text-rose-700 border-rose-200'
                                                            }`}>
                                                            {(s.is_activate === 1 || s.is_activate === true) ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-1 py-1 align-middle text-right pr-4">
                                                    <div className="flex justify-end items-center gap-1.5 pr-1">
                                                        {canEdit && (
                                                            <>
                                                                <button
                                                                    onClick={() => openModal(s)}
                                                                    className="text-slate-300 hover:text-blue-500 transition-colors active:scale-95"
                                                                    title="แก้ไข"
                                                                >
                                                                    <Pencil size={13} strokeWidth={2.5} />
                                                                </button>
                                                                <div className="w-[1px] h-2.5 bg-slate-100" />
                                                                <button
                                                                    onClick={() => handleDelete(s.id)}
                                                                    className="text-slate-300 hover:text-rose-500 transition-colors active:scale-95"
                                                                    title="ลบ"
                                                                >
                                                                    <Trash2 size={13} strokeWidth={2.5} />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination Controls */}
                        {totalItems > 0 && (
                            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/30 flex flex-col md:flex-row items-center justify-between gap-4">
                                <div className="flex items-center gap-2">
                                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">แสดงรายการ:</span>
                                    <div className="relative">
                                        <button
                                            type="button"
                                            onClick={() => setIsRowsDropdownOpen(!isRowsDropdownOpen)}
                                            className="bg-white border border-slate-200 rounded-lg px-2 py-1 min-w-[55px] flex items-center justify-between gap-1.5 shadow-sm hover:border-slate-300 hover:bg-slate-50 outline-none  focus:ring-2 focus:ring-blue-50"
                                        >
                                            <span className="text-[12px] font-bold text-slate-700">{rowsPerPage}</span>
                                            <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform duration-200 ${isRowsDropdownOpen ? 'rotate-180 text-blue-500' : ''}`} />
                                        </button>

                                        {isRowsDropdownOpen && (
                                            <>
                                                <div className="fixed inset-0 z-40" onClick={() => setIsRowsDropdownOpen(false)} />
                                                <div className="absolute bottom-full left-0 mb-2 min-w-full w-max bg-white border border-slate-100 rounded-2xl shadow-2xl z-50 overflow-hidden animate-in fade-in zoom-in-95 slide-in-from-bottom-2 duration-200">
                                                    <div className="py-0.5">
                                                        {[10, 20, 50, 100].map(size => (
                                                            <button
                                                                key={size}
                                                                onClick={() => {
                                                                    setRowsPerPage(size);
                                                                    setIsRowsDropdownOpen(false);
                                                                    setCurrentPage(1);
                                                                }}
                                                                className={`w-full px-3 py-1.5 text-[12px] font-bold flex items-center justify-between transition-colors ${rowsPerPage === size ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                                                            >
                                                                <span>{size}</span>
                                                                {rowsPerPage === size && <Check className="w-3 h-3" />}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider ml-2">
                                        {(currentPage - 1) * rowsPerPage + 1}-{Math.min(currentPage * rowsPerPage, totalItems)} จาก {totalItems}
                                    </span>
                                </div>

                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => setCurrentPage(1)}
                                        disabled={currentPage === 1}
                                        className={`p-1.5 rounded-lg  ${currentPage === 1 ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:bg-slate-100 hover:text-blue-600 active:scale-95'}`}
                                        title="First Page"
                                    >
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" /></svg>
                                    </button>
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={currentPage === 1}
                                        className={`p-1.5 rounded-lg  ${currentPage === 1 ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:bg-slate-100 hover:text-blue-600 active:scale-95'}`}
                                        title="Previous Page"
                                    >
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" /></svg>
                                    </button>

                                    <div className="flex items-center gap-1 px-2">
                                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                            let pageNum;
                                            if (totalPages <= 5) pageNum = i + 1;
                                            else if (currentPage <= 3) pageNum = i + 1;
                                            else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                            else pageNum = currentPage - 2 + i;

                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => setCurrentPage(pageNum)}
                                                    className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-black  ${currentPage === pageNum ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'}`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                        disabled={currentPage === totalPages}
                                        className={`p-1.5 rounded-lg  ${currentPage === totalPages ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:bg-slate-100 hover:text-blue-600 active:scale-95'}`}
                                        title="Next Page"
                                    >
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" /></svg>
                                    </button>
                                    <button
                                        onClick={() => setCurrentPage(totalPages)}
                                        disabled={currentPage === totalPages}
                                        className={`p-1.5 rounded-lg  ${currentPage === totalPages ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:bg-slate-100 hover:text-blue-600 active:scale-95'}`}
                                        title="Last Page"
                                    >
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Modal */}
            {
                isModalOpen && (
                    <div className="fixed inset-0 bg-slate-900/70 flex items-center justify-center z-[2000] p-4">
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md md:max-w-[420px] max-h-[90vh] overflow-hidden flex flex-col z-10 animate-in zoom-in-95 fade-in duration-200 border border-slate-200 text-slate-800">
                            {/* Header */}
                            <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
                                <div>
                                    <h2 className="text-sm font-bold text-slate-800 tracking-tight">
                                        {editingSlot ? 'แก้ไขช่องจอด' : 'เพิ่มช่องจอดใหม่'}
                                    </h2>
                                    <p className="text-xs font-medium text-slate-400 mt-0.5">ระบุรายละเอียดช่องจอด</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-600  font-bold"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            {/* Modal Content */}
                            <form onSubmit={handleSave} noValidate autoComplete="off" className="flex flex-col flex-1 overflow-hidden">
                                <div className="p-4 space-y-4 overflow-y-auto flex-1">
                                    <div className="space-y-4">
                                        <div className="space-y-1">
                                            <label className="text-[10px] font-bold text-slate-500  tracking-wider uppercase">ชื่อช่องจอด <span className="text-rose-500 font-bold">*</span></label>
                                            <input
                                                className={`w-full text-[13px] font-medium text-slate-700 px-3 py-1.5 rounded-lg border focus:ring-0 outline-none placeholder:text-slate-300  ${error && !newSlot.slot_name.trim() ? 'border-rose-500 bg-rose-50/20' : 'border-slate-200 focus:border-slate-300'}`}
                                                value={newSlot.slot_name}
                                                onChange={(e) => {
                                                    setNewSlot({ ...newSlot, slot_name: e.target.value });
                                                    if (error) setError('');
                                                }}
                                                required
                                                placeholder="เช่น A01, B05"
                                            />
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-bold text-slate-500  tracking-wider uppercase">โซน <span className="text-rose-500 font-bold">*</span></label>
                                                <div className="relative">
                                                    <button
                                                        type="button"
                                                        onClick={() => setIsZoneDropdownOpen(!isZoneDropdownOpen)}
                                                        onBlur={() => setTimeout(() => setIsZoneDropdownOpen(false), 200)}
                                                        className={`w-full bg-white border rounded-lg shadow-sm flex items-center px-3 py-1.5 gap-2 text-left group outline-none ${error && !newSlot.zone_id ? 'border-rose-500 bg-rose-50/20' : 'border-slate-200 hover:border-slate-300'}`}
                                                    >
                                                        <span className={`text-[13px] font-medium truncate flex-1 ${newSlot.zone_id ? 'text-slate-700' : 'text-slate-400'}`}>
                                                            {zones.find(z => String(z.id) === String(newSlot.zone_id))?.zone_name || 'เลือกโซน'}
                                                        </span>
                                                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isZoneDropdownOpen ? 'rotate-180 text-blue-500' : 'text-slate-400'}`} />
                                                    </button>
                                                    {isZoneDropdownOpen && (
                                                        <div className="absolute top-full mt-1.5 w-full bg-white border border-slate-100 rounded-2xl shadow-2xl z-[70] overflow-hidden animate-in fade-in zoom-in-95 slide-in-from-top-2 duration-200">
                                                            <div className="max-h-[200px] overflow-y-auto py-1 scrollbar-hide">
                                                                {zones.map(z => (
                                                                    <button
                                                                        key={z.id}
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setNewSlot({ ...newSlot, zone_id: z.id });
                                                                            setIsZoneDropdownOpen(false);
                                                                            if (error) setError('');
                                                                        }}
                                                                        className={`w-full text-left px-4 py-2 text-[13px] font-medium flex items-center justify-between transition-colors ${String(newSlot.zone_id) === String(z.id) ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                                                                    >
                                                                        <span>{z.zone_name} {!z.is_active ? '(Inactive)' : ''}</span>
                                                                        {String(newSlot.zone_id) === String(z.id) && <Check className="w-4 h-4 text-blue-600" />}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-bold text-slate-500  tracking-wider uppercase">ประเภทรถ</label>
                                                <div className="relative">
                                                    <button
                                                        type="button"
                                                        onClick={() => setIsVehicleTypeDropdownOpen(!isVehicleTypeDropdownOpen)}
                                                        className="w-full bg-white border border-slate-200 rounded-lg shadow-sm flex items-center px-3 py-1.5 gap-2 text-left group outline-none hover:border-slate-300"
                                                    >
                                                        <span className={`text-[13px] font-medium truncate flex-1 ${newSlot.vehicle_type ? 'text-slate-700' : 'text-slate-400'}`}>
                                                            {newSlot.vehicle_type === 'Car' ? 'รถยนต์' : newSlot.vehicle_type === 'Motorcycle' ? 'รถจักรยานยนต์' : 'ระบุประเภทรถ'}
                                                        </span>
                                                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isVehicleTypeDropdownOpen ? 'rotate-180 text-blue-500' : 'text-slate-400'}`} />
                                                    </button>
                                                    {isVehicleTypeDropdownOpen && (
                                                        <>
                                                            <div className="fixed inset-0 z-[65]" onClick={() => setIsVehicleTypeDropdownOpen(false)} />
                                                            <div className="absolute top-full mt-1.5 w-full bg-white border border-slate-100 rounded-2xl shadow-2xl z-[70] overflow-hidden animate-in fade-in zoom-in-95 slide-in-from-top-2 duration-200">
                                                                <div className="py-1">
                                                                    {[
                                                                        { label: 'รถยนต์', value: 'Car' },
                                                                        { label: 'รถจักรยานยนต์', value: 'Motorcycle' }
                                                                    ].map(opt => (
                                                                        <button
                                                                            key={opt.value}
                                                                            type="button"
                                                                            onClick={() => {
                                                                                setNewSlot({ ...newSlot, vehicle_type: opt.value });
                                                                                setIsVehicleTypeDropdownOpen(false);
                                                                            }}
                                                                            className={`w-full text-left px-4 py-2 text-[13px] font-medium flex items-center justify-between transition-colors ${newSlot.vehicle_type === opt.value ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                                                                        >
                                                                            <span>{opt.label}</span>
                                                                            {newSlot.vehicle_type === opt.value && <Check className="w-4 h-4 text-blue-600" />}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-bold text-slate-500 tracking-wider uppercase">รูปแบบจอด</label>
                                                <div className="relative">
                                                    <button
                                                        type="button"
                                                        onClick={() => setIsDoubleParkingDropdownOpen(!isDoubleParkingDropdownOpen)}
                                                        className="w-full bg-white border border-slate-200 rounded-lg shadow-sm flex items-center px-3 py-1.5 gap-2 text-left group outline-none hover:border-slate-300"
                                                    >
                                                        <span className="text-[13px] font-medium truncate flex-1 text-slate-700">
                                                            {(newSlot.is_double_parking === 1 || newSlot.is_double_parking === true) ? 'ซ้อนคัน' : 'ปกติ'}
                                                        </span>
                                                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isDoubleParkingDropdownOpen ? 'rotate-180 text-blue-500' : 'text-slate-400'}`} />
                                                    </button>
                                                    {isDoubleParkingDropdownOpen && (
                                                        <>
                                                            <div className="fixed inset-0 z-[65]" onClick={() => setIsDoubleParkingDropdownOpen(false)} />
                                                            <div className="absolute top-full mt-1.5 min-w-full w-max bg-white border border-slate-100 rounded-2xl shadow-2xl z-[70] overflow-hidden animate-in fade-in zoom-in-95 slide-in-from-bottom-2 duration-200">
                                                                <div className="py-1">
                                                                    {[
                                                                        { label: 'ปกติ', value: false },
                                                                        { label: 'ซ้อนคัน', value: true }
                                                                    ].map(opt => (
                                                                        <button
                                                                            key={opt.label}
                                                                            type="button"
                                                                            onClick={() => {
                                                                                setNewSlot({ ...newSlot, is_double_parking: opt.value });
                                                                                setIsDoubleParkingDropdownOpen(false);
                                                                            }}
                                                                            className={`w-full text-left px-4 py-2 text-[13px] font-medium flex items-center justify-between transition-colors ${(newSlot.is_double_parking === 1 || newSlot.is_double_parking === true) === opt.value ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                                                                        >
                                                                            <span>{opt.label}</span>
                                                                            {(newSlot.is_double_parking === 1 || newSlot.is_double_parking === true) === opt.value && <Check className="w-4 h-4 text-blue-600" />}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="space-y-1">
                                                <label className="text-[10px] font-bold text-slate-500 tracking-wider uppercase">สถานะ</label>
                                                <div className="relative">
                                                    <button
                                                        type="button"
                                                        onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
                                                        className="w-full bg-white border border-slate-200 rounded-lg shadow-sm flex items-center px-3 py-1.5 gap-2 text-left group outline-none hover:border-slate-300"
                                                    >
                                                        <span className="text-[13px] font-medium truncate flex-1 text-slate-700">
                                                            {(newSlot.is_activate === 1 || newSlot.is_activate === true) ? 'Active' : 'Inactive'}
                                                        </span>
                                                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isStatusDropdownOpen ? 'rotate-180 text-blue-500' : 'text-slate-400'}`} />
                                                    </button>
                                                    {isStatusDropdownOpen && (
                                                        <>
                                                            <div className="fixed inset-0 z-[65]" onClick={() => setIsStatusDropdownOpen(false)} />
                                                            <div className="absolute top-full mt-1.5 min-w-full w-max bg-white border border-slate-100 rounded-2xl shadow-2xl z-[70] overflow-hidden animate-in fade-in zoom-in-95 slide-in-from-bottom-2 duration-200">
                                                                <div className="py-1">
                                                                    {[
                                                                        { label: 'Active', value: true },
                                                                        { label: 'Inactive', value: false }
                                                                    ].map(opt => (
                                                                        <button
                                                                            key={opt.label}
                                                                            type="button"
                                                                            onClick={() => {
                                                                                setNewSlot({ ...newSlot, is_activate: opt.value });
                                                                                setIsStatusDropdownOpen(false);
                                                                            }}
                                                                            className={`w-full text-left px-4 py-2 text-[13px] font-medium flex items-center justify-between transition-colors ${(newSlot.is_activate === 1 || newSlot.is_activate === true) === opt.value ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                                                                        >
                                                                            <span>{opt.label}</span>
                                                                            {(newSlot.is_activate === 1 || newSlot.is_activate === true) === opt.value && <Check className="w-4 h-4 text-blue-600" />}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="space-y-1">
                                            <label className="text-[10px] font-bold text-slate-500  tracking-wider uppercase">หมายเหตุ (ถ้ามี)</label>
                                            <textarea
                                                className="w-full text-[13px] font-medium text-slate-700 px-3 py-1.5 rounded-lg border border-slate-200 focus:border-slate-300 focus:ring-0 outline-none  placeholder:text-slate-300 resize-none h-20"
                                                value={newSlot.remark || ''}
                                                onChange={(e) => setNewSlot({ ...newSlot, remark: e.target.value })}
                                                placeholder="รายละเอียด..."
                                            />
                                        </div>

                                        {/* Coordinate Preview (Read-only) */}
                                        {(newSlot.coordinate_x !== null || newSlot.coordinate_y !== null) && (
                                            <div className="bg-slate-50 rounded-xl p-3 border border-slate-100 flex items-center justify-between">
                                                <div className="flex items-center gap-2 text-slate-500">
                                                    <MapPin size={14} />
                                                    <span className="text-[10px] font-bold tracking-wider">Position on Map</span>
                                                </div>
                                                <div className="flex gap-4 font-mono text-[11px] font-black text-blue-600">
                                                    <span>X: {newSlot.coordinate_x !== null ? Number(newSlot.coordinate_x).toFixed(1) : '-'}%</span>
                                                    <span>Y: {newSlot.coordinate_y !== null ? Number(newSlot.coordinate_y).toFixed(1) : '-'}%</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Error Message */}
                                {error && (
                                    <div className="mx-5 mb-4 p-3 bg-rose-50 border border-rose-100 rounded-xl flex items-center gap-3 text-rose-600 animate-in slide-in-from-bottom-2 duration-200 shrink-0">
                                        <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                                            <AlertTriangle size={18} strokeWidth={2.5} />
                                        </div>
                                        <span className="text-xs leading-tight font-medium">{error}</span>
                                    </div>
                                )}

                                {/* Footer */}
                                <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex gap-3 shrink-0">
                                    <button
                                        type="button"
                                        onClick={() => setIsModalOpen(false)}
                                        className="flex-1 px-4 py-2.5 rounded-xl text-slate-600 text-sm font-bold hover:bg-slate-100 border border-slate-200 active:scale-95  outline-none"
                                    >
                                        ยกเลิก
                                    </button>
                                    <button
                                        type="submit"
                                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl text-sm font-bold shadow-lg shadow-blue-500/25 active:scale-95  outline-none"
                                    >
                                        {editingSlot ? 'บันทึกแก้ไข' : 'บันทึก'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }

            {/* Delete Confirmation Popup */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 z-[2010] flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-slate-900/70 animate-in fade-in duration-300"
                        onClick={() => setShowDeleteConfirm(false)}
                    />
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-[340px] overflow-hidden z-20 animate-in zoom-in-95 fade-in duration-300 border border-slate-100">
                        <div className="p-10 flex flex-col items-center text-center">
                            <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center mb-6">
                                <Trash2 className="w-8 h-8 text-rose-500" />
                            </div>
                            <h3 className="text-xl font-black text-slate-800 tracking-tight">ยืนยันการลบข้อมูล?</h3>
                            <p className="text-[13px] text-slate-400 mt-2">ข้อมูลนี้จะถูกลบถาวร ไม่สามารถกู้คืนได้</p>

                            <div className="flex gap-3 w-full mt-10">
                                <button
                                    onClick={() => setShowDeleteConfirm(false)}
                                    className="flex-1 py-2.5 border border-slate-100 text-slate-500 rounded-2xl font-black text-[14px]  active:scale-95 hover:bg-slate-50 shadow-sm"
                                >
                                    ยกเลิก
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    className="flex-1 py-2.5 bg-rose-500 text-white rounded-2xl font-black text-[14px] shadow-lg shadow-rose-200  active:scale-95 hover:bg-rose-600  tracking-wide"
                                >
                                    ลบข้อมูล
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Popup Modal */}
            {showSuccessPopup && (
                <div className="fixed inset-0 z-[2010] flex items-center justify-center p-4 text-center">
                    <div
                        className="absolute inset-0 bg-slate-900/70 animate-in fade-in duration-300"
                        onClick={() => setShowSuccessPopup(false)}
                    />
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-[320px] overflow-hidden z-20 animate-in zoom-in-95 fade-in duration-300 border border-slate-100">
                        <div className="p-10 flex flex-col items-center">
                            <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mb-6 shadow-inner">
                                <svg className="w-8 h-8 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                            </div>
                            <h3 className="text-xl font-black text-slate-800 tracking-tight">ทำรายการสำเร็จ</h3>
                            <p className="text-[13px] font-bold text-slate-400 mt-2 leading-relaxed">{successMessage}</p>

                            <button
                                onClick={() => setShowSuccessPopup(false)}
                                className="mt-8 w-full py-2.5 bg-blue-600 text-white rounded-2xl font-black text-[14px] tracking-wide  active:scale-95 hover:bg-blue-700 shadow-lg shadow-blue-500/20"
                            >
                                ตกลง
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Error Popup Modal */}
            {showErrorPopup && (
                <div className="fixed inset-0 z-[2010] flex items-center justify-center p-4 text-center">
                    <div
                        className="absolute inset-0 bg-slate-900/70 animate-in fade-in duration-300"
                        onClick={() => setShowErrorPopup(false)}
                    />
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-[320px] overflow-hidden z-20 animate-in zoom-in-95 fade-in duration-300 border border-slate-100">
                        <div className="p-10 flex flex-col items-center">
                            <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center mb-6 shadow-inner">
                                <AlertCircle className="w-8 h-8 text-rose-500" />
                            </div>
                            <h3 className="text-xl font-black text-rose-600 tracking-tight">เกิดข้อผิดพลาด</h3>
                            <p className="text-[13px] font-bold text-slate-400 mt-2 leading-relaxed">{errorMessage}</p>

                            <button
                                onClick={() => setShowErrorPopup(false)}
                                className="mt-8 w-full py-2.5 bg-slate-800 text-white rounded-2xl font-black text-[14px] tracking-wide  active:scale-95 hover:bg-slate-900 shadow-lg shadow-slate-200"
                            >
                                เข้าใจแล้ว
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div >
    );
};

export default SlotManagement;
